import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API, setAuthToken } from '@/App';
import { Eye, EyeOff } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Kayıtlı bilgileri yükle
    const savedUsername = localStorage.getItem('snaplink_admin_username');
    const savedPassword = localStorage.getItem('snaplink_admin_password');
    if (savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      });

      if (response.data.user.role !== 'admin') {
        toast.error('Admin girişi gerekli');
        setLoading(false);
        return;
      }

      // Beni hatırla
      if (rememberMe) {
        localStorage.setItem('snaplink_admin_username', username);
        localStorage.setItem('snaplink_admin_password', password);
      } else {
        localStorage.removeItem('snaplink_admin_username');
        localStorage.removeItem('snaplink_admin_password');
      }

      setAuthToken(response.data.token);
      toast.success('Giriş başarılı!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] gradient-bg flex items-center justify-center p-4 safe-area-inset" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <motion.div 
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="flex items-center justify-center gap-3 mb-4"
          >
            <img src="/sl-logo.png" alt="SnapLink" className="w-16 h-16 rounded-xl" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white">SnapLink</h1>
          <p className="text-purple-500 text-sm">Admin Paneli</p>
        </div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="card-glass p-6 sm:p-8"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Admin Girişi</h2>
          <p className="text-gray-400 mb-6 text-sm">Yönetim paneline erişim</p>

          <form onSubmit={handleLogin} data-testid="admin-login-form">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">Kullanıcı Adı</label>
              <input
                data-testid="admin-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-modern"
                placeholder="admin"
                required
                autoComplete="username"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">Şifre</label>
              <div className="relative">
                <input
                  data-testid="admin-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-modern pr-12"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="mb-6 flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500 focus:ring-2"
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-400">
                Beni Hatırla
              </label>
            </div>

            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full btn-purple py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="back-btn"
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-purple-500 transition-colors"
            >
              ← Ana Sayfaya Dön
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
